import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * A workspace's monthly spend cap, edited (plan-014 E3b; FR12-ENT-06) — used by Finance → Allowances and caps and by
 * Workspace detail, so both say the same thing and send the same change.
 *
 * Three facts, kept apart as FR12-ENT-06 asks: the CAP (money), what happens AT THE CAP (stop new calls / allow a set
 * overage, then stop / only warn) and the WARNING (a percentage that notifies and never stops anything). The change
 * names the revision it read (`0` creates the cap); a stale one is refused `409 revision_stale` and the page re-reads.
 * "A cap is not money set aside" — the prototype's own reminder, under the form.
 */
import { useMutation } from "@tanstack/react-query";
import { Button, Field, Input, Select } from "@wetalk/ui";
import { ApiError } from "@wetalk/api-client";
import { useState, type FormEvent, type ReactNode } from "react";

import { useGateway } from "../../../api/provider.js";
import type { SpendCapBehaviorView, SpendCapChangedView, SpendCapView } from "../../../api/slice/entitlement.js";
import { CAP_BEHAVIOR_CHOICE, capAmountFieldOf, capAmountMinorOf, entitlementRefusalTextOf, warnPercentOf } from "./allowance-copy.js";

import "./allowance.css";

/** The warning a new cap starts with — the prototype's "Warn at 80%"; the person changes it before saving. */
const NEW_CAP_WARN_PERCENT = "80";

export interface SpendCapFormProps {
  readonly workspaceId: string;
  readonly workspaceName: string;
  /** The cap as read, or `null` when the workspace has none yet (the change then creates it). */
  readonly current: SpendCapView | null;
  readonly onSaved: (changed: SpendCapChangedView) => void;
  /** A stale revision: the caller re-reads the cap. */
  readonly onStale: () => void;
  readonly onCancel: () => void;
}

export function SpendCapForm(props: SpendCapFormProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const { current } = props;
  const [amount, setAmount] = useState(current === null ? "" : capAmountFieldOf(current.monthBudgetMinor));
  const [behavior, setBehavior] = useState<SpendCapBehaviorView>(current === null ? "hard_stop" : current.capBehavior);
  const [overage, setOverage] = useState(current === null || current.overageMinor === null ? "" : capAmountFieldOf(current.overageMinor));
  const [warn, setWarn] = useState(current === null ? NEW_CAP_WARN_PERCENT : String(current.warnAtPercent));
  const amountMinor = capAmountMinorOf(amount);
  const overageMinor = behavior === "capped_overage" ? capAmountMinorOf(overage) : null;
  const warnPercent = warnPercentOf(warn);
  const choice = CAP_BEHAVIOR_CHOICE.find((one) => one.value === behavior);

  const saving = useMutation({
    mutationFn: () => {
      if (amountMinor === null || warnPercent === null || (behavior === "capped_overage" && overageMinor === null)) {
        throw new Error(portalText("finance", "m0408"));
      }
      return gateway.changeSpendCap(
        props.workspaceId,
        { monthBudgetMinor: amountMinor, capBehavior: behavior, overageMinor, warnAtPercent: warnPercent },
        current === null ? 0 : current.revision,
      );
    },
    onSuccess: (changed) => props.onSaved(changed),
    onError: (error) => {
      if (error instanceof ApiError && error.code === "revision_stale") props.onStale();
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    saving.mutate();
  };
  const invalid = amountMinor === null || warnPercent === null || (behavior === "capped_overage" && overageMinor === null);
  return (
    <form className="al-cap-form" onSubmit={submit} aria-label={portalText("finance", "m0409", { value1: props.workspaceName })} noValidate>
      <Field label={portalText("finance", "m0410")} {...(amount !== "" && amountMinor === null ? { error: portalText("finance", "m0411") } : {})}>
        {(control) => (
          <Input
            {...control}
            value={amount}
            inputMode="decimal"
            placeholder="€2,000.00"
            onChange={(event) => {
              setAmount(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      <Field label={portalText("finance", "m0393")} {...(choice === undefined ? {} : { description: choice.dek })}>
        {(control) => (
          <Select
            aria-label={portalText("finance", "m0393")}
            {...control}
            value={behavior}
            onChange={(event) => {
              setBehavior(event.target.value as SpendCapBehaviorView);
            }}
          >
            {CAP_BEHAVIOR_CHOICE.map((one) => (
              <option key={one.value} value={one.value}>
                {one.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {behavior === "capped_overage" ? (
        <Field label={portalText("finance", "m0412")} description={portalText("finance", "m0413")} {...(overage !== "" && overageMinor === null ? { error: portalText("finance", "m0414") } : {})}>
          {(control) => (
            <Input
              {...control}
              value={overage}
              inputMode="decimal"
              placeholder="€50.00"
              onChange={(event) => {
                setOverage(event.currentTarget.value);
              }}
            />
          )}
        </Field>
      ) : null}
      <Field
        label={portalText("finance", "m0394")}
        description={portalText("finance", "m0415")}
        {...(warn !== "" && warnPercent === null ? { error: portalText("finance", "m0416") } : {})}
      >
        {(control) => (
          <Input
            {...control}
            value={warn}
            inputMode="numeric"
            placeholder="80"
            onChange={(event) => {
              setWarn(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      <p className="al-cap-note">{portalText("finance", "m0417")}</p>
      {saving.isError ? (
        <p className="al-error" role="alert">
          {entitlementRefusalTextOf(saving.error, "cap")}
        </p>
      ) : null}
      <div className="al-actions">
        <Button variant="ghost" type="button" disabled={saving.isPending} onClick={props.onCancel}>{portalText("finance", "m0418")}</Button>
        <Button variant="primary" type="submit" disabled={invalid || saving.isPending}>
          {current === null ? portalText("finance", "m0419") : portalText("finance", "m0420")}
        </Button>
      </div>
    </form>
  );
}
